import { useMemo } from 'react';
import type { Euler } from 'three';

import {
  usePlayerActorScene,
  usePlayerResolvedAnimation,
  usePlayerSettings,
  usePlayerTimeline,
} from '@cartwhl/3d/player';

import { useStore as useMotionEditorStore } from '../../motionEditorStore';
import {
  getSourceBvhForTimeline,
  normalizePlaybackFrame,
  playbackFrameToKeyPoseSourceFrame,
  resolveKeyPosePlaybackFrame,
  sourceFrameToPlaybackFrame,
} from '../keyPoseFrameMapping';
import {
  resolveActiveKeyPoseReferenceName,
  resolveSelectedKeyPoseMotionForReference,
} from '../keyPoseMotionUtils';
import {
  hasKeyPoseBoneSelection,
  selectKeyPoseRecordsForReference,
} from '../keyPoseSelectors';
import { captureKeyPoseSnapshotFromSourceScene } from '../keyPoseSnapshots';
import { useStore as useKeyPoseStore } from '../keyPoseStore';
import type { KeyPoseRecord } from '../types';

const TRANSIENT_KEY_POSE_ID_PREFIX = 'transient-key-pose:';

interface UseCurrentFrameKeyPoseEditingInput {
  sceneRotation?: Euler;
}

export function useCurrentFrameKeyPoseEditing({
  sceneRotation,
}: UseCurrentFrameKeyPoseEditingInput) {
  const motionEditorStore = useMotionEditorStore();
  const keyPoseStore = useKeyPoseStore();
  const timeline = usePlayerTimeline();
  const {
    settings: {
      playback: { frameRate: playbackFrameRate },
    },
  } = usePlayerSettings();
  const { playback } = motionEditorStore.state;
  const referenceName = resolveActiveKeyPoseReferenceName(
    keyPoseStore.state.activeReferenceName,
    playback.selectedReferenceName,
    playback.sceneActors,
  );
  const actorScene = usePlayerActorScene(referenceName ?? undefined);
  const resolvedAnimation = usePlayerResolvedAnimation(
    referenceName ?? undefined,
  );
  const selectedMotion = referenceName
    ? resolveSelectedKeyPoseMotionForReference(
        referenceName,
        playback.selectedReferenceName,
        playback.selectedSceneMotion,
        playback.sceneActors,
      )
    : null;
  const sourceBvh = getSourceBvhForTimeline(
    resolvedAnimation.bvh,
    resolvedAnimation.bvhs,
    selectedMotion?.timelineIndex,
  );
  const maxPlaybackFrame = Math.max(timeline.duration.frames - 1, 0);
  const currentPlaybackFrame = normalizePlaybackFrame(
    timeline.currentFrame,
    maxPlaybackFrame,
  );
  const currentEditablePlaybackFrame = resolveKeyPosePlaybackFrame({
    bvh: sourceBvh,
    frame: currentPlaybackFrame,
    maxPlaybackFrame,
    playbackFrameRate,
  });
  const hasEditingSelection = hasKeyPoseBoneSelection(
    keyPoseStore.state.editing,
  );
  const keyPoseAtCurrentPlaybackFrame = useMemo(() => {
    return (
      selectKeyPoseRecordsForReference(keyPoseStore.state, referenceName).find(
        (candidateKeyPose) =>
          sourceFrameToPlaybackFrame({
            bvh: sourceBvh,
            frame: candidateKeyPose.frame,
            maxPlaybackFrame,
            playbackFrameRate,
          }) === currentPlaybackFrame,
      ) ?? null
    );
  }, [
    currentPlaybackFrame,
    keyPoseStore.state,
    maxPlaybackFrame,
    playbackFrameRate,
    referenceName,
    sourceBvh,
  ]);
  const keyPoseAtEditablePlaybackFrame = useMemo(() => {
    return (
      selectKeyPoseRecordsForReference(keyPoseStore.state, referenceName).find(
        (candidateKeyPose) =>
          sourceFrameToPlaybackFrame({
            bvh: sourceBvh,
            frame: candidateKeyPose.frame,
            maxPlaybackFrame,
            playbackFrameRate,
          }) === currentEditablePlaybackFrame,
      ) ?? null
    );
  }, [
    currentEditablePlaybackFrame,
    keyPoseStore.state,
    maxPlaybackFrame,
    playbackFrameRate,
    referenceName,
    sourceBvh,
  ]);
  const transientKeyPose = useMemo<KeyPoseRecord | null>(() => {
    if (
      timeline.isPlaying ||
      keyPoseAtEditablePlaybackFrame ||
      !hasEditingSelection ||
      !referenceName
    ) {
      return null;
    }

    const sourceFrame = playbackFrameToKeyPoseSourceFrame({
      bvh: sourceBvh,
      frame: currentPlaybackFrame,
      maxPlaybackFrame,
      playbackFrameRate,
    });
    const capturedKeyPose = captureKeyPoseSnapshotFromSourceScene({
      bvh: sourceBvh,
      frame: sourceFrame,
      scene: actorScene,
    });
    if (!capturedKeyPose) {
      return null;
    }

    return {
      id: `${TRANSIENT_KEY_POSE_ID_PREFIX}${referenceName}:${capturedKeyPose.frame}`,
      frame: capturedKeyPose.frame,
      stamp: 'user',
      skeletonSnapshot: capturedKeyPose.skeletonSnapshot,
      sourcePose: capturedKeyPose.sourcePose,
    };
  }, [
    actorScene,
    currentPlaybackFrame,
    hasEditingSelection,
    keyPoseAtEditablePlaybackFrame,
    maxPlaybackFrame,
    playbackFrameRate,
    referenceName,
    sourceBvh,
    timeline.isPlaying,
  ]);
  const keyPose = timeline.isPlaying
    ? null
    : hasEditingSelection
      ? (keyPoseAtEditablePlaybackFrame ?? transientKeyPose)
      : keyPoseAtCurrentPlaybackFrame;

  return {
    actorScene,
    isPlaying: timeline.isPlaying,
    keyPose,
    keyPoseAtCurrentPlaybackFrame,
    keyPoseIsTransient:
      keyPose === transientKeyPose && transientKeyPose !== null,
    referenceName,
    sceneRotation,
    sourceBvh,
  };
}
